import useApiClient from "../../useApiClient";
import { useSelector } from "react-redux";
import { useCallback } from "react";
import { withNetworkErrorHandling } from "../../helpers/withNetworkErrorHandling";

const useTaskAPIClient = () => {
  const { set, remove, update } = useApiClient();
  const { activeUser } = useSelector((state) => state.user);

  const createTask = useCallback(
    async ({ task }) => {
      return await withNetworkErrorHandling(() =>
        set(`users/${activeUser.userId}/tasks`, task),
      );
    },
    [activeUser, set],
  );

  const updateTask = useCallback(
    async ({ taskId, patches }) => {
      return await withNetworkErrorHandling(() =>
        update(`users/${activeUser.userId}/tasks/${taskId}`, patches),
      );
    },
    [activeUser, update],
  );

  const completeTask = useCallback(
    async (taskId) => {
      const completedPatch = {
        op: "replace",
        path: "/completed",
        value: true,
      };
      return await withNetworkErrorHandling(() =>
        update(`users/${activeUser.userId}/tasks/${taskId}`, [completedPatch]),
      );
    },
    [activeUser, update],
  );

  const cancelTask = useCallback(
    async (taskId) => {
      return await withNetworkErrorHandling(() =>
        remove(`users/${activeUser.userId}/tasks/${taskId}`),
      );
    },
    [activeUser, remove],
  );

  const setTaskTargeted = useCallback(
    async (taskId, targeted) => {
      const patches = [
        {
          op: "replace",
          path: "/targeted",
          value: targeted,
        },
      ];

      // When targeting a task, set targetedAt to current time
      if (targeted) {
        patches.push({
          op: "replace",
          path: "/targetedAt",
          value: new Date().toISOString(),
        });
      }

      return await withNetworkErrorHandling(() =>
        update(`users/${activeUser.userId}/tasks/${taskId}`, patches),
      );
    },
    [activeUser, update],
  );

  const setTaskAttribute = useCallback(
    async (taskId, attribute, value) => {
      const setTaskAttributePatch = {
        op: "replace",
        path: `/attributes/${attribute}`,
        value: value,
      };
      return await withNetworkErrorHandling(() =>
        update(`users/${activeUser.userId}/tasks/${taskId}`, [
          setTaskAttributePatch,
        ]),
      );
    },
    [activeUser, update],
  );

  const setNoteCompletionStatus = useCallback(
    async (taskId, noteId, completed) => {
      const setNoteCompletionStatusPatch = {
        op: "replace",
        path: `/notes/${noteId}/completed`,
        value: completed,
      };
      return await withNetworkErrorHandling(() =>
        update(`users/${activeUser.userId}/tasks/${taskId}`, [
          setNoteCompletionStatusPatch,
        ]),
      );
    },
    [activeUser, update],
  );

  const removeNote = useCallback(
    async (taskId, noteId) => {
      const removeNotePatch = {
        op: "remove",
        path: `/notes/${noteId}`,
      };
      return await withNetworkErrorHandling(() =>
        update(`users/${activeUser.userId}/tasks/${taskId}`, [removeNotePatch]),
      );
    },
    [activeUser, update],
  );

  return {
    createTask,
    updateTask,
    cancelTask,
    completeTask,
    setTaskTargeted,
    setTaskAttribute,
    setNoteCompletionStatus,
    removeNote,
  };
};

export default useTaskAPIClient;
